import React from 'react';
import { Toaster } from 'react-hot-toast';

import { useRecoilValue } from 'recoil';
import Honeybadger from '@honeybadger-io/js';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { datadogRum } from '@datadog/browser-rum';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import useViewportHeight from 'lib/hooks/useViewportHeight';
import useKioskTouchMode from 'lib/hooks/useKioskTouchMode';
import useAutoDocumentTitle from 'lib/hooks/useAutoDocumentTitle';

import AutoLogout from 'components/AutoLogout/AutoLogout';
import AutoIdle from 'components/Kiosk/components/AutoIdle';
import ErrorBoundary from 'components/shared/Error/Boundary';

import state from './components/state';
import { Routes } from './routes/Routes';
import Utility from './components/Utility';
import AutoUpdate from './components/AutoUpdate/AutoUpdate';

import './styles/main.scss';

const discardedRumErrorMessages = [
  '[Honeybadger]',
  'useImperativeHandle',
  'Ignored attempt to cancel a touch',
  'ResizeObserver loop',
  'Object Not Found Matching Id',
  'runtime.sendMessage',
  'Script error.',
  'Unexpected end of script',
  'failed to connect afer'
];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 2 // 2 minutes
    }
  }
});

const App = () => {
  if (
    (process.env.ENV === 'development' || process.env.ENV === 'production') &&
    window.__APP_VERSION__
  ) {
    datadogRum.init({
      applicationId: 'cb6d45cb-947a-48d9-9bb8-d1d15a109db0',
      clientToken: 'pubbc5c7e531a26bd07f2c4b08e6a90bc77',
      site: 'datadoghq.com',
      service: 'myriad-health',
      env: process.env.ENV,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      version: window.__APP_VERSION__,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      enablePrivacyForActionName: true,
      traceContextInjection: 'all',
      allowedTracingUrls: [(url) => url.startsWith(process.env.REACT_APP_BASE_API_URL)],
      traceSampleRate: 100,
      beforeSend: (event, context) => {
        try {
          if (
            event.type === 'error' &&
            discardedRumErrorMessages.some((message) => event.error?.message?.includes(message))
          ) {
            return false;
          }
          // iOS 18.7 WebKit throws stackless null-`<minified>.id` TypeErrors from the
          // Session Replay DOM recorder; frameless stack = not our code, unactionable.
          if (
            event.type === 'error' &&
            event.error?.type === 'TypeError' &&
            !String(event.error?.stack || '').includes('\n') &&
            /null is not an object \(evaluating '\w{1,3}\.id'\)/.test(event.error?.message || '')
          ) {
            return false;
          }
          if (event.type !== 'resource' || event.resource.type !== 'xhr') {
            return true;
          }
          // Capture response headers if available
          const xhr = context?.xhr;
          if (!xhr) {
            return true;
          }
          event.context.responseHeaders = Object.fromEntries(
            xhr
              .getAllResponseHeaders()
              .split('\r\n')
              .filter((header) => header)
              .map((header) => header.split(': ').map((item) => item.trim()))
          );
        } catch (e) {
          console.error(e);
          Honeybadger.notify('Datadog error: ', e);
        }
        return true;
      }
    });
  }

  // eslint-disable-next-line import/no-named-as-default-member -- atoms accessed by member
  const hasBeenAuthChecked = useRecoilValue(state.hasBeenAuthChecked);
  useAutoDocumentTitle();
  useViewportHeight();
  useKioskTouchMode();

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Utility />
        {/*<Socket />*/}
        {hasBeenAuthChecked.checked && <Routes />}
        <Toaster position="bottom-right" containerStyle={{ top: 64, right: 24 }} />
        {process.env.ENV !== 'local' && <AutoLogout />}
        <AutoIdle />
        <AutoUpdate />
      </ErrorBoundary>
      {/* Enable TanStack Query Devtools only if needed, comment Devtool after you finish debugging */}
      {/* <ReactQueryDevtools /> */}
    </QueryClientProvider>
  );
};

export default App;
export { queryClient };
